import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Wand2, Loader2, Send, ShieldCheck, Sparkles, CheckCircle2, ArrowRight, Eraser } from "lucide-react";
import { errMsg, useSSA } from "../SSAContext";
import { useAuth } from "../../contexts/AuthContext";
import FieldInput, { isVisible } from "../components/FieldInput";
import AssessmentPanel from "../components/AssessmentPanel";
import { Card, DemoStamp, ReadinessBadge } from "../ui";

function Overview({ svc }) {
  return (
    <Card eyebrow={svc.family} title={svc.name} testid="service-overview">
      <img src={svc.image} alt={svc.name} className="w-full h-44 object-cover rounded-lg mb-4" data-testid="service-overview-img" />
      <p className="text-sm text-slate-600 leading-relaxed">{svc.summary}</p>
      <div className="mt-4 text-[10px] uppercase tracking-wider text-slate-400 font-bold">Typical outputs</div>
      <div className="flex flex-wrap gap-1.5 mt-1.5">{svc.outputs.map((o) => <span key={o} className="text-xs bg-slate-100 rounded px-2 py-0.5">{o}</span>)}</div>
      <div className="mt-4 text-[10px] uppercase tracking-wider text-slate-400 font-bold">{svc.category === "FM" ? "Scope elements" : "Channels"}</div>
      <div className="text-sm text-slate-700">{svc.channels.join(" · ")}</div>
      <div className="mt-5 rounded-lg bg-[#0B2A4A] text-white p-4 text-xs leading-relaxed">
        <div className="font-bold text-[#7DD3FC] uppercase tracking-wider text-[10px] mb-1">{svc.pricing_note}</div>
        Final scope and price are confirmed through your tailored request and partner quotations. No fixed catalogue price, no pre-negotiated rates.
        {svc.category === "FM" ? " Partners quote a monthly fee against your SLAs. Materials, consumables and call-outs are shown separately at cost." : " Media, creator, licence and other pass-through costs are always shown separately."}
      </div>
      {svc.slas?.length > 0 && (
        <div className="mt-4" data-testid="service-sla-reference">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Typical SLA framework</div>
          {svc.slas.map(([m, t]) => <div key={m} className="flex justify-between text-xs border-b border-slate-100 py-1"><span className="text-slate-600">{m}</span><span className="font-semibold">{t}</span></div>)}
        </div>
      )}
      <ol className="mt-5 space-y-2 text-xs text-slate-600">
        {["AI checks your brief for completeness & contradictions", "Infosys Procurement validates and invites 3–4 qualified partners", "Partners propose privately — your identity is never shared", "You compare up to two options and issue one PO to Infosys"].map((s, i) => (
          <li key={s} className="flex gap-2"><span className="h-5 w-5 shrink-0 rounded-full bg-slate-100 text-slate-700 font-bold flex items-center justify-center text-[10px]">{i + 1}</span>{s}</li>
        ))}
      </ol>
    </Card>
  );
}

function BuyerProfile({ country, setCountry, isDemo, setIsDemo }) {
  const { user } = useAuth();
  const { config } = useSSA();
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 grid md:grid-cols-4 gap-3 text-xs" data-testid="buyer-profile">
      <div><div className="text-slate-400 font-bold uppercase tracking-wider text-[10px]">Buyer (pre-filled)</div><div className="font-semibold text-slate-900">{user?.display_name}</div></div>
      <div><div className="text-slate-400 font-bold uppercase tracking-wider text-[10px]">Company account</div><div className="font-semibold text-slate-900">{user?.tenant}</div></div>
      <div>
        <label htmlFor="delivery-country" className="text-slate-400 font-bold uppercase tracking-wider text-[10px]">Delivery country</label>
        <select id="delivery-country" value={country} onChange={(e) => setCountry(e.target.value)} className="w-full mt-0.5 rounded border border-slate-200 bg-white px-2 py-1" data-testid="delivery-country-select">
          {Object.entries(config?.countries || {}).map(([k, c]) => <option key={k} value={k}>{c.name} · {c.currency}</option>)}
        </select>
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={isDemo} onChange={(e) => setIsDemo(e.target.checked)} data-testid="demo-record-toggle" /> Demo record (synthetic)
      </label>
      <div className="md:col-span-4 flex items-center gap-1.5 text-emerald-700"><ShieldCheck size={13} /> Buyer name, company, email and phone are internal-only and are stripped from every partner payload.</div>
    </div>
  );
}

function Submitted({ res }) {
  return (
    <Card testid="request-submitted">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="text-emerald-600 shrink-0" />
        <div className="flex-1">
          <div className="font-display text-xl font-semibold text-slate-900">Request submitted to Infosys Procurement</div>
          <div className="text-sm text-slate-600 mt-1">Reference <span className="font-mono font-bold" data-testid="submitted-ref">{res.id}</span> · <ReadinessBadge value={res.readiness} /></div>
          <div className="mt-4 text-[10px] uppercase tracking-wider text-slate-400 font-bold">Suggested routing (procurement confirms)</div>
          <div className="grid sm:grid-cols-4 gap-2 mt-2">
            {res.routing_preview.map((r) => (
              <div key={r.partner_id} className="rounded-lg border border-slate-200 p-3" data-testid={`routing-preview-${r.partner_id}`}>
                <div className="font-semibold text-sm">{r.label}</div><div className="text-xs text-slate-500">{r.profile}</div>
                <div className="text-xs mt-1">Fit score <b>{r.score}</b></div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-5">
            <Link to={`/services/requests/${res.id}`} className="inline-flex items-center gap-1 bg-[#0B2A4A] text-white text-sm font-semibold px-4 py-2 rounded-lg" data-testid="track-request-link">Track request <ArrowRight size={14} /></Link>
            <Link to={`/services/workbench/${res.id}`} className="inline-flex items-center gap-1 border border-slate-300 text-sm font-semibold px-4 py-2 rounded-lg" data-testid="open-workbench-link">Switch to Procurement view</Link>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function ServicePage() {
  const { sid } = useParams();
  const { call, setActiveRid, bump, category, setCategory } = useSSA();
  const [svc, setSvc] = useState(null);
  const [answers, setAnswers] = useState({});
  const [country, setCountry] = useState("NL");
  const [isDemo, setIsDemo] = useState(true);
  const [assessment, setAssessment] = useState(null);
  const [assessedFor, setAssessedFor] = useState(null);
  const [busy, setBusy] = useState("");
  const [submitted, setSubmitted] = useState(null);
  const [deep, setDeep] = useState(false);
  const assessedForRef = useRef(null);
  useEffect(() => { assessedForRef.current = assessedFor; }, [assessedFor]);
  const countryRef = useRef(country);
  useEffect(() => { countryRef.current = country; }, [country]);
  const draftKey = `ssa_draft_${sid}`;

  useEffect(() => {
    setSubmitted(null); setAssessment(null);
    call("get", `/catalog/${sid}?country=${countryRef.current}`).then((d) => {
      setSvc(d);
      const saved = sessionStorage.getItem(draftKey);
      setAnswers(saved ? JSON.parse(saved) : {});
    }).catch((e) => toast.error(errMsg(e)));
  }, [sid, call, draftKey]);

  useEffect(() => {
    if (svc && svc.category !== category) setCategory(svc.category);
  }, [svc, category, setCategory]);

  // Tab-scoped draft only; internal cost centre is never persisted client-side
  useEffect(() => {
    if (!svc) return;
    const { cost_centre: _omit, ...safe } = answers;
    sessionStorage.setItem(draftKey, JSON.stringify(safe));
  }, [answers, svc, draftKey]);

  const setAnswer = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));
  const flagged = useMemo(() => new Set([...(assessment?.missing || []).map((m) => m.field), ...(assessment?.contradictions || []).flatMap((c) => c.fields || [])]), [assessment]);
  const key = JSON.stringify(answers) + country;

  const fillDemo = async () => {
    const d = await call("get", `/catalog/${sid}?country=${country}`);
    setAnswers(d.demo_fixture);
    toast.success("Filled with service-specific synthetic demo data");
  };
  const runAssess = async () => {
    setBusy("assess");
    try {
      const a = await call("post", "/ai/assess", { service_id: sid, answers, country });
      setAssessment(a); setAssessedFor(key);
      if (a.job_id && a.readiness !== "incomplete") pollJob(a.job_id, key);
      return a;
    } catch (e) { toast.error(errMsg(e)); return null; } finally { setBusy(""); }
  };
  const pollJob = async (jobId, forKey) => {
    setDeep(true);
    for (let i = 0; i < 60; i += 1) {
      await new Promise((r) => setTimeout(r, 4000));
      try {
        const j = await call("get", `/ai/jobs/${jobId}`);
        if (j.status === "done") {
          setAssessment((cur) => (assessedForRef.current === forKey ? { ...j.result, job_id: jobId } : cur));
          break;
        }
      } catch { break; }
    }
    setDeep(false);
  };
  const submit = async () => {
    const a = assessedFor === key ? assessment : await runAssess();
    if (!a) return;
    if (a.readiness === "incomplete") { toast.error("Complete the highlighted fields before submitting"); return; }
    setBusy("submit");
    try {
      const r = await call("post", "/requests", { service_id: sid, answers, country, is_demo: isDemo, assessment: a });
      setSubmitted(r); setActiveRid(r.id); bump();
      sessionStorage.removeItem(draftKey);
      toast.success(`Submitted ${r.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  if (!svc) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-400" /></div>;
  if (submitted) return <Submitted res={submitted} />;
  const common = svc.common_fields.filter((f) => isVisible(f, answers));
  return (
    <div className="grid xl:grid-cols-12 gap-6" data-testid="service-page">
      <div className="xl:col-span-4 space-y-4"><Overview svc={svc} /></div>
      <div className="xl:col-span-8 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-display text-lg font-semibold text-slate-900 mr-auto">Tailored brief · <span className="font-mono text-sm text-slate-500">{svc.id} schema v{svc.schema_version}</span></h2>
          <button onClick={fillDemo} className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100" data-testid="fill-demo-data-btn"><Sparkles size={14} /> Fill with Demo Data</button>
          <button onClick={() => { setAnswers({}); setAssessment(null); }} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white" data-testid="clear-form-btn"><Eraser size={14} /> Clear</button>
        </div>
        <BuyerProfile country={country} setCountry={setCountry} isDemo={isDemo} setIsDemo={setIsDemo} />
        <Card eyebrow={`${svc.service_fields.length} service-specific questions`} title={`${svc.name.split("(")[0]} – scope details`} testid="service-fields-card">
          <div className="grid md:grid-cols-2 gap-4">{svc.service_fields.map((f) => <FieldInput key={f.key} f={f} answers={answers} setAnswer={setAnswer} flagged={flagged.has(f.key)} />)}</div>
        </Card>
        <Card eyebrow={`${common.length} shared request fields`} title={svc.category === "FM" ? "Sites, operating model, staff transfer, SLAs, budget & compliance" : "Objectives, markets, timeline, budget & governance"} testid="common-fields-card">
          <div className="grid md:grid-cols-2 gap-4">{svc.common_fields.map((f) => <FieldInput key={f.key} f={f} answers={answers} setAnswer={setAnswer} flagged={flagged.has(f.key)} />)}</div>
        </Card>
        <Card eyebrow="Claude Opus 5.5 · advisory, never invents values" title="AI quotation-readiness check" testid="ai-check-card"
          action={<button onClick={runAssess} disabled={!!busy} className="inline-flex items-center gap-1.5 bg-[#0072CE] hover:bg-[#005BA6] text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60" data-testid="run-ai-check-btn">{busy === "assess" ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Run AI check</button>}>
          {deep && <div className="mb-3 inline-flex items-center gap-2 text-xs font-semibold text-[#0072CE] bg-sky-50 border border-sky-200 rounded-full px-3 py-1" data-testid="ai-deep-review-running"><Loader2 size={12} className="animate-spin" /> Preliminary rule check shown — Claude Opus 5.5 category-expert review in progress, readiness may change</div>}
          {assessment ? <AssessmentPanel a={assessment} /> : <p className="text-sm text-slate-500">{svc.category === "FM" ? "Checks sites, floor area, operating hours, contract term, staff transfer and collective-agreement exposure, SLAs and compliance. It flags mobilisation risks, classifies readiness and drafts the sanitised partner brief." : "Checks completeness, dates, budgets, platforms, markets and KPIs; flags contradictions; classifies readiness and drafts the sanitised partner brief."}</p>}
          {assessment && assessedFor !== key && <p className="text-xs text-amber-700 mt-3">Answers changed since the last check – it will re-run on submit.</p>}
        </Card>
        <div className="flex items-center justify-between gap-3 bg-white rounded-xl border border-slate-200 p-4">
          {isDemo ? <DemoStamp text="Demo request – no real RFQs, emails or POs are sent" /> : <span className="text-xs text-slate-500">Live request – partner routing remains simulated until partner onboarding is approved.</span>}
          <button onClick={submit} disabled={!!busy} className="inline-flex items-center gap-1.5 bg-[#0B2A4A] hover:bg-[#123C66] text-white font-semibold px-5 py-2.5 rounded-lg disabled:opacity-60" data-testid="submit-request-btn">
            {busy === "submit" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {isDemo ? "Submit Demo Request" : "Submit request"}
          </button>
        </div>
      </div>
    </div>
  );
}
